import { STORAGE_NAMESPACE } from "../config";
import type { SeatRole } from "../core";
import { type KeptStore, keptOrMade } from "./identity";

/**
 * What one browser tab remembers between its reloads (B3.7): the guest ID the rooms know it by,
 * the room match it sits in, the count of its games ended (B3.10) and begun (G2), which the
 * journal's gate events read, and the seat its next front page takes (G44). `sessionStorage`, not
 * the save: two tabs of one browser are two players (B3.6), and a reload of either comes back to
 * its own seat. A storage that throws
 * (private mode, a blocked frame) only means nothing is remembered. The kept guest across visits
 * (B3.10) is `src/front/identity.ts`'s, for events only; the rooms keep the tab's.
 */
const GUEST_KEY = `${STORAGE_NAMESPACE}:guest`;
const MATCH_KEY = `${STORAGE_NAMESPACE}:match`;

function tab(): Storage | undefined {
  try {
    return sessionStorage;
  } catch {
    return undefined;
  }
}

/** The tab's storage as a kept store; with none, nothing is kept. */
const TAB_STORE: KeptStore = {
  get: (key) => tab()?.getItem(key) ?? null,
  set: (key, value) => tab()?.setItem(key, value),
};

/** This tab's guest ID: the kept one, or a new one, kept at once. */
export function tabGuestId(fresh: () => string): string {
  return keptOrMade(TAB_STORE, GUEST_KEY, fresh);
}

/** The room match this tab sits in; undefined forgets it. */
export function rememberMatch(code: string | undefined): void {
  try {
    if (code === undefined) {
      tab()?.removeItem(MATCH_KEY);
    } else {
      tab()?.setItem(MATCH_KEY, code);
    }
  } catch {
    // Not remembered: a reload plays the duel against scripts, as before B3.7.
  }
}

const ENDED_KEY = `${STORAGE_NAMESPACE}:ended`;
const STARTED_KEY = `${STORAGE_NAMESPACE}:started`;
const COUNTED_ROOM_KEY = `${STORAGE_NAMESPACE}:countedRoom`;

/** One more in the tab's count under `key`; 0 when the store keeps nothing or throws. */
function countOneMore(store: KeptStore, key: string): number {
  try {
    const count = Number(store.get(key) ?? 0) + 1;
    store.set(key, String(count));
    return store.get(key) === String(count) ? count : 0;
  } catch {
    return 0;
  }
}

/**
 * B3.10: one more match ended in this tab; returns the count, which outlives the page a room
 * match leaves by "Play again". A storage that keeps nothing counts nothing (0): no prompt.
 */
export function countEndedMatch(store: KeptStore = TAB_STORE): number {
  return countOneMore(store, ENDED_KEY);
}

/**
 * G2: one more game begun in this tab, a run or a room match; returns the count, which outlives
 * the page a room match leaves by "Play again" (D-108: Play now's game is a room match, played
 * once a page). A room match (`room`, its code) begun again by a reload or a drop-in is the same
 * game: undefined, as for a tab that keeps nothing.
 */
export function countStartedGame(
  room: string | undefined,
  store: KeptStore = TAB_STORE,
): number | undefined {
  try {
    if (room !== undefined && store.get(COUNTED_ROOM_KEY) === room) {
      return undefined;
    }
    const count = countOneMore(store, STARTED_KEY);
    if (count === 0) {
      return undefined;
    }
    if (room !== undefined) {
      store.set(COUNTED_ROOM_KEY, room);
    }
    return count;
  } catch {
    return undefined;
  }
}

const NEXT_SEAT_KEY = `${STORAGE_NAMESPACE}:nextSeat`;

/**
 * G44: the seat picked by a room match's "Change role", kept for the front page the tab loads
 * next, which takes it at the company's table at once. A store that keeps nothing keeps no seat:
 * that page waits for Play now, as before.
 */
export function keepNextSeat(seat: SeatRole, store: KeptStore = TAB_STORE): void {
  try {
    store.set(NEXT_SEAT_KEY, seat);
  } catch {
    // Not kept: the front page waits for Play now.
  }
}

/** G44: the seat kept for this front page, taken once: a reload of the page finds none. */
export function takeNextSeat(store: KeptStore = TAB_STORE): SeatRole | undefined {
  try {
    const kept = store.get(NEXT_SEAT_KEY);
    if (kept === null) {
      return undefined;
    }
    store.set(NEXT_SEAT_KEY, "");
    return kept === "king" || kept === "soldier" ? kept : undefined;
  } catch {
    return undefined;
  }
}

export function rememberedMatch(): string | undefined {
  try {
    return tab()?.getItem(MATCH_KEY) ?? undefined;
  } catch {
    return undefined;
  }
}
